import { useCompany } from "@/contexts/CompanyContext";
import { useDirtyNav } from "@/contexts/DirtyNavContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Building2, ChevronDown, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const CompanySwitcher = () => {
  const { companies, currentCompany, selectCompany } = useCompany();
  const { confirm } = useDirtyNav();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!currentCompany) return null;

  // Switching company while inside an add/edit screen is dangerous — a save would
  // hit the WRONG company. We always confirm unsaved changes first, then redirect
  // to /dashboard instead of staying on a URL whose IDs belong to the old company.
  const handleSelect = async (id: string) => {
    if (id === currentCompany.id) return;
    const ok = await confirm();
    if (!ok) return;
    await selectCompany(id);
    navigate("/dashboard", { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-1.5 sm:gap-2 min-w-0 max-w-[45vw] sm:max-w-[280px] px-2 sm:px-3">
          <Building2 className="h-4 w-4 shrink-0" />
          <span className="truncate text-sm">{currentCompany.name}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>{t("companies.switchCompany")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {companies.map((c) => (
          <DropdownMenuItem key={c.id} onClick={() => handleSelect(c.id)} className="flex justify-between gap-2">
            <span className="truncate flex-1">{c.name}</span>
            <span className="text-xs text-muted-foreground">{t(`roles.${c.role}`)}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/companies/new")}>
          <Plus className="me-2 h-4 w-4" /> {t("companies.createCompany")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

